import { useEffect, useState } from 'react';
import UpsertSmartContractModal from '../../../components/modals/smart-contracts/UpsertSmartContractModal';
import { Button } from '@oc-labs/wt-storybook';
import { AlertTriangle, Building2, Eye, EyeClosed, Plus, ShieldCheck, ShieldX } from "lucide-react";
import useModal from '../../../hooks/useModal';
import useSmartContracts from '../../../hooks/useSmartContracts';
import useAPI from '../../../hooks/useAPI';
import toast from 'react-hot-toast';
import DeleteModal from '../../../components/modals/DeleteModal';
import useNetworks from '../../../hooks/useNetworks';
import Tooltip from '../../../components/Tooltip';

const REQUIRED_CONTRACT_FIELDS = [
  { key: 'abiURL', label: 'ABI' },
  { key: 'decimals', label: 'Decimals' },
  { key: 'address', label: 'Address' },
  { key: 'networkId', label: 'Network' },
];

const getMissingFields = (contract) => {
  return REQUIRED_CONTRACT_FIELDS
    .filter(({ key }) => {
      const value = contract[key];
      if (value === undefined || value === null) return true;
      if (typeof value === 'string') return !value.trim();
      return false;
    })
    .map(({ label }) => label);
};

export default function AdminSmartContracts() {
  const api = useAPI();
  const { adminNetworks } = useNetworks();
  const { adminSmartContracts, adminDeleteSmartContract } = useSmartContracts();
  const { open: openCreateModal } = useModal('create-smart-contract-modal');
  const { open: openDeleteModal } = useModal('delete-modal');
  const [contracts, setContracts] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [abis, setAbis] = useState({});
  const [selectedNetwork, setSelectedNetwork] = useState('all');

  useEffect(() => {
    loadContracts();
    adminNetworks().then((res) => setNetworks(res)).catch(() => {
      toast.error('Failed to load networks');
    });
    api.getAbis().then((res) => setAbis(res || {})).catch(() => {
      toast.error('Failed to load ABIs');
    });
  }, []);

  const loadContracts = () => adminSmartContracts()
    .then((res) => setContracts(res))
    .catch((err) => toast.error(err.message || 'Failed to load smart contracts'));

  const handleRemoveContract = (contractId) => {
    openDeleteModal({
      title: 'Delete Smart Contract',
      text: 'Are you sure you want to delete this smart contract? This action cannot be undone.',
      onDelete: async () => {
        try {
          await adminDeleteSmartContract(contractId);
          toast.success('Smart contract deleted successfully');
          loadContracts();
        } catch (error) {
          toast.error(error.message || 'Failed to delete smart contract');
        }
      }
    });
  }

  const filteredContracts = (selectedNetwork === 'all'
    ? contracts
    : contracts.filter(contract => contract.networkId === selectedNetwork)
  ).slice().sort((a, b) => {
    const nameCompare = (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    if (nameCompare !== 0) return nameCompare;
    return (a.id || '').localeCompare(b.id || '');
  });


  return (<>
    <UpsertSmartContractModal onUpsert={loadContracts} networks={networks} abis={abis} />
    <DeleteModal />
    <div className="2xl:p-6 p-3">

      <div className='flex justify-between items-center mb-4'>
        <div className='flex gap-2 items-center flex-wrap'>
          <button
            onClick={() => setSelectedNetwork('all')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
              selectedNetwork === 'all'
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All Networks
          </button>
          {networks.map((network) => (
            <button
              key={network.id}
              onClick={() => setSelectedNetwork(network.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                selectedNetwork === network.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {network.name}
            </button>
          ))}
        </div>
        <div className='flex gap-3'>
          <Button
            type="secondary"
            size="small"
            onClick={() => openCreateModal()}
            icon={<Plus size={16} />}
            className="whitespace-nowrap"
          >
            Create Smart Contract
          </Button>
        </div>
      </div>
      <div className="rounded-lg shadow overflow-hidden">
        <table className="w-full bg-white table-fixed">
          <thead className="bg-gray-100 uppercase">
            <tr>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[8%]">Status</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]">Name</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[8%]">Symbol</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[39%]">Address</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]">Network</th>
              <th className="2xl:py-3 py-2 2xl:px-6 px-3 text-left text-xs font-medium tracking-wider w-[15%]"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredContracts.map((contract) => (
              <tr key={contract.id} className="hover:bg-gray-50 transition">
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3">
                  <div className='flex items-center gap-2'>
                    <div className='rounded-full w-4 h-4 bg-gray-200 flex items-center justify-center shrink-0'>
                      <div className={`rounded-full w-2 h-2 ${contract.enabled ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    </div>
                    <div>
                      {contract.displayDefault ? <Eye className="text-gray-400 w-4" /> : <EyeClosed className="text-gray-400 w-4" />}
                    </div>
                    <div>
                      {contract.requiresKyc ? <ShieldCheck className="text-blue-500 w-4" /> : <ShieldX className="text-gray-300 w-4" />}
                    </div>
                  </div>
                </td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">
                  {(() => {
                    const missing = getMissingFields(contract);
                    const companyLabel = contract.company?.name || contract.companyId;
                    return (
                      <div className="flex items-center gap-2">
                        {missing.length > 0 && (
                          <Tooltip text={`Missing: ${missing.join(', ')}`}>
                            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
                          </Tooltip>
                        )}
                        {contract.companyId && (
                          <Tooltip text={`Company contract${companyLabel ? `: ${companyLabel}` : ''}`}>
                            <Building2 className="w-4 h-4 text-indigo-500 shrink-0" />
                          </Tooltip>
                        )}
                        <span className="truncate">{contract.name}</span>
                      </div>
                    );
                  })()}
                </td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs font-semibold">
                  <div className="flex flex-col items-start gap-1 min-w-0">
                    <span className="truncate w-full">{contract.symbol}</span>
                    {contract.fixedPrice !== null && contract.fixedPrice !== undefined && (
                      <Tooltip text="Fixed USD price configured for this contract">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-700 whitespace-nowrap">
                          {`Fixed: $${contract.fixedPrice}`}
                        </span>
                      </Tooltip>
                    )}
                    {contract.buyable && (
                      <Tooltip
                        text={
                          contract.purchaseFee !== null && contract.purchaseFee !== undefined
                            ? `Buyable (fee: ${(Number(contract.purchaseFee) * 100).toFixed(2)}%)`
                            : "Buyable"
                        }
                      >
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-sky-100 text-sky-700 whitespace-nowrap">
                          {contract.purchaseFee !== null && contract.purchaseFee !== undefined
                            ? `Buyable · ${(Number(contract.purchaseFee) * 100).toFixed(2)}%`
                            : "Buyable"}
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs font-mono truncate">{contract.address}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs truncate">{contract.network?.name || contract.networkId}</td>
                <td className="2xl:py-4 py-2.5 2xl:px-6 px-3 text-xs">
                  <div className='flex items-center gap-2'>
                    <Button
                      onClick={() => openCreateModal(contract)}
                      size="small"
                      type="primary"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => handleRemoveContract(contract.id)}
                      size="small"
                      type="danger"
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </>);
}
